<template>
  <section class="mx-3 lg:mx-0 max-w-screen-2xl">
    <div class="mt-10 lg:w-3/5 mx-auto">
      <div class="flex flex-col gap-10">
        <p
          class="text-lg text-root-2 font-extrabold mt-6"
          id="settingsTourItem2"
        >
          API Keys
        </p>
        <div class="border-main rounded-lg">
          <div v-if="isLoading">
            <api-keys-loader />
          </div>
          <div v-else>
            <div class="lg:px-8 px-3 pb-5">
              <div>
                <el-form
                  label-position="top"
                  :model="apiKeyForm"
                  ref="apiKeyForm"
                  label-width="80px"
                  size="normal"
                  :rules="rules"
                >
                  <el-form-item
                    class="pt-4 -pb-4"
                    label="Create API Secret"
                    prop="apiSecret"
                  >
                    <div class="note mt-2 relative bottom-9 ml-32">
                      <el-popover
                        placement="top-start"
                        title="Note"
                        width="460"
                        trigger="hover"
                        :content="content"
                        style="word-break: normal"
                      >
                        <p slot="reference"><warning-icon-grey /></p>
                      </el-popover>
                    </div>
                    <el-input
                      placeholder="Enter API Secret"
                      name="apiSecret"
                      @keydown.enter.native.prevent="validateForm('apiKeyForm')"
                      show-password
                      v-model="apiKeyForm.apiSecret"
                    ></el-input>
                  </el-form-item>
                  <div class="flex justify-end mb-6">
                    <div
                      @click="validateForm('apiKeyForm')"
                      class="w-2/5 cursor-pointer border border-primary rounded-lg text-primary lg:text-sm text-xs py-4 font-normal flex items-center justify-center hover-animation-subtle"
                    >
                      {{ hasApiKey ? 'Update API Keys' : 'Generate API Keys' }}
                    </div>
                  </div>
                  <!-- Show keys-->
                  <div v-if="hasApiKey" class="border-t pt-12">
                    <div>
                      <div>
                        <p class="text-13px font-normal text-fg-muted pb-3">
                          Public Keys
                        </p>
                        <div class="grid grid-cols-12 gap-x-3">
                          <p
                            style="word-break: break-all"
                            class="lg:col-span-5 col-span-10 text-root-2 lg:text-base text-sm font-normal"
                          >
                            {{ maskPublicKey(apiKeyInformation.key) }}
                          </p>
                          <div
                            class="cursor-pointer col-span-1 flex items-center"
                            v-clipboard:copy="apiKeyInformation.key"
                            v-clipboard:success="onCopy"
                            v-clipboard:error="onError"
                          >
                            <copy-icon />
                          </div>
                        </div>
                      </div>
                      <div class="mt-8 pb-8">
                        <p class="text-13px font-normal text-fg-muted pb-3">
                          Bearer Token
                        </p>
                        <div class="grid grid-cols-12 gap-x-3">
                          <p
                            style="word-break: break-all"
                            class="lg:col-span-5 col-span-10 text-root-2 lg:text-base text-sm font-normal"
                          >
                            {{ maskBearerToken(apiKeyInformation.token) }}
                          </p>
                          <div
                            class="cursor-pointer col-span-1 flex items-center"
                            v-clipboard:copy="apiKeyInformation.token"
                            v-clipboard:success="onCopy"
                            v-clipboard:error="onError"
                          >
                            <copy-icon />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </el-form>
              </div>
            </div>
          </div>
        </div>

        <div v-if="isDevEnvironment" class="border-main rounded-lg pt-4 px-6">
          <el-form :model="webhookForm" ref="webhookForm" size="normal">
            <el-form-item prop="notificationUrl">
              <div class="flex justify-between items-center w-full">
                <div class="flex items-center">
                  <p class="text-base font-light">Webhook</p>
                </div>
                <div class="flex items-center justify-end">
                  <div
                    @click="manageWebhooks"
                    class="w-full px-3 cursor-pointer border border-primary rounded-lg text-primary lg:text-sm text-xs py-4 font-normal flex items-center justify-center hover-animation-subtle"
                  >
                    <settings-icon :active="true" :color="$primaryColor" />
                    <p class="px-2">
                      {{
                        hasApiNoWebhook || noApiNoWebhook
                          ? 'Configure Webhook'
                          : 'Manage Webhooks'
                      }}
                    </p>
                  </div>
                </div>
              </div>
            </el-form-item>
          </el-form>
        </div>
      </div>
    </div>
    <verify-auth-dialog
      v-if="showVerifyAuthDialog"
      :authState="authState"
      :showDialog="showVerifyAuthDialog"
      @closeDialog="closeAuthDialog"
      @generateApiKeys="generateApiKeys"
    />
    <app-tour
      :tourDetails="settingsTour"
      tourComponent="settings"
      :displayTour="displayTour"
      @stopTour="stopTour"
    />
  </section>
</template>

<script>
import { MaskPublicKey, MaskBearerToken } from '@/utils/helpers'
import { API_SECRET_TIP_INFO, SETTINGS_TOUR } from '@/utils/constants'
import getApiCredentials from '@/mixins/externalApi.js'

export default {
  mixins: [getApiCredentials],
  data () {
    return {
      displayTour: false,
      settingsTour: SETTINGS_TOUR,
      showVerifyAuthDialog: false,
      isLoading: false,
      authState: 'generateKeyState',
      content: API_SECRET_TIP_INFO,
      apiKeyForm: {
        apiSecret: ''
      },
      webhookForm: {
        notificationUrl: ''
      },
      rules: {
        apiSecret: [
          {
            required: true,
            message: 'Please enter a secret key',
            trigger: ['blur', 'change']
          }
        ]
      },
      webhookRules: {
        notificationUrl: [
          {
            required: true,
            message: 'Please enter a webhook url',
            trigger: ['blur', 'change']
          }
        ]
      }
    }
  },
  components: {
    AppTour: () => import('@/components/v2/app-tour/AppTour.vue'),
    WarningIconGrey: () => import('@/assets/icons/WarningIconGrey.vue'),
    CopyIcon: () => import('@/assets/icons/CopyIcon.vue'),
    VerifyAuthDialog: () => import('@/components/v2/auth/VerifyAuthDialog'),
    SettingsIcon: () => import('@/assets/icons/SettingsIcon.vue'),
    ApiKeysLoader: () =>
      import('@/components/v2/skeletalLoaders/ApiKeysLoader.vue')
  },
  methods: {
    startSettingsTour () {
      this.$tours.SettingsTour.start(this.currentTourIndex)
      this.displayTour = true
    },
    stopTour () {
      this.displayTour = false
      this.$store.dispatch('appTour/updateSettingsTourStatus', false)
    },
    maskBearerToken (val) {
      return MaskBearerToken(val)
    },
    maskPublicKey (val) {
      return MaskPublicKey(val)
    },
    onCopy: function (e) {
      this.$notify.success({
        title: 'Copied'
      })
    },
    onError: function (e) {
      this.$notify.error('Error copying to clipboard!')
    },
    showAuthDialog () {
      this.showVerifyAuthDialog = true
    },
    closeAuthDialog () {
      this.showVerifyAuthDialog = false
    },
    validateForm (formName) {
      this.$refs[formName].validate((valid) => {
        if (valid) {
          if (formName === 'webhookForm') {
            this.saveWebhook()
          } else {
            this.showAuthDialog()
          }
        } else {
          this.$notify.error({
            title: 'Incomplete details',
            message: 'Please fill the missing Information',
            position: 'top-right'
          })
        }
      })
    },
    async generateApiKeys (twoFaCode) {
      this.isLoading = true
      const meta = {
        key: this.apiKeyForm.apiSecret,
        twoFaCode: twoFaCode.code
      }
      this.closeAuthDialog()
      const vm = this
      await this.$store
        .dispatch('externalApi/generateApiKey', meta)
        .then(() => {
          this.getApiCredentials()
          this.$notify({
            title: 'Success',
            message: 'API Keys Generated',
            type: 'success'
          })
        })
        .catch((error) => {
          const err = error.response
            ? error.response.data.error.publicMessage
            : error
          vm.$swal({
            icon: 'error',
            title: 'Oops',
            text: err,
            iconColor: '#F26722',
            allowOutsideClick: false
          })
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    async saveWebhook () {
      this.isLoading = true
      const vm = this
      const info = {
        url: this.webhookForm.notificationUrl
      }
      await this.$store
        .dispatch('externalApi/saveWebhook', info)
        .then(() => {
          this.isLoading = true
          this.getApiCredentials()
          this.$notify({
            title: 'Success',
            message: 'Webhook Saved',
            type: 'success'
          })
        })
        .catch((error) => {
          const err = error.response
            ? error.response.data.error.publicMessage
            : error
          vm.$swal({
            icon: 'error',
            title: 'Oops',
            text: err,
            iconColor: '#F26722',
            allowOutsideClick: false
          })
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    manageWebhooks () {
      if (this.hasApiNoWebhook) {
        this.saveWebhook()
      } else if (this.hasApiAndWebhook) {
        this.$router.push('/manage-webhooks')
      }
    }
  },
  async beforeMount () {
    await this.getApiCredentials()
    if (this.$store.state.appTour.settingsTourIsActive === true) {
      this.startSettingsTour()
    }
  },
  computed: {
    currentTourIndex () {
      return this.$store.state.appTour.currentSettingsTourIndex
    },
    apiKeyInformation () {
      return this.$store.state.externalApi.apiCredentials || {}
    },
    hasApiKey () {
      return this.$store.state.externalApi.apiCredentials.token
    },
    webhookUrl () {
      return this.$store.state.externalApi.apiCredentials.webhook || ''
    },
    isDevEnvironment () {
      return process.env.VUE_APP_DEV_INDICATOR
    },
    hasApiNoWebhook () {
      return !this.webhookUrl && this.hasApiKey
    },
    noApiNoWebhook () {
      return !this.webhookUrl && !this.hasApiKey
    },
    hasApiAndWebhook () {
      return this.webhookUrl && this.hasApiKey
    }
  }
}
</script>

<style scoped>
ul {
  list-style-type: circle;
}

>>> .el-popover {
  word-break: normal !important;
}
.note {
  width: 0;
  height: 0;
}
.no-cursor {
  cursor: not-allowed;
}
</style>
